import styled from "styled-components";
import { useState, useContext, useEffect } from "react";
import { DialogWindowContext } from "../../../Contexts/DialogueWindowContext";
import { UsersDatabaseContext } from "../../../Contexts/UserDatabaseContext";
import { OrgTreeContext } from "../../../Contexts/OrgTreeContext";
import OrgTreeSelect from "../../UI/OrgTreeSelect/OrgTreeSelect";
import TeamMemberRow from "./TeamMemberRow";
import { Tooltip } from "primereact/tooltip";
import TeamSettings from "./TeamSettings/TeamSettings";
import AddTeamMemeber from "./AddTeamMember/AddTeamMember";
import { getAllUserIdsFromOrgTreeNode } from "../../../Helpers/OrgTreeFunctions";

const ManageTeamsWrapper = styled.div`
  margin: 10px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const TeamMembersList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  border: 1px solid var(--accent-color-dark);
  width: 90%;
`;

const Header = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  background-color: var(--accent-color-dark);
  color: white;
  padding: 10px 5px;
`;

const TeamMembersTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnHeaders = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px 80px 80px;
  padding: 10px 0;
  justify-items: center;
  align-items: center;
  border-bottom: 1px solid var(--accent-color-dark);
  font-weight: bold;
  text-align: center;
  @media (max-width: 550px) {
    grid-template-columns: 1fr 1fr 50px;
    padding: 7px 0px;
  }
`;

const AddMemberRow = styled(ColumnHeaders)`
  grid-template-columns: 1fr;
  color: var(--accent-color-dark);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-bottom: none;

  &:hover {
    background-color: var(--accent-color-dark);
    color: white;
  }
`;

export default function ManageTeams() {
  const userDatabase = useContext(UsersDatabaseContext);
  const users = userDatabase?.usersDatabase;
  const orgTree = useContext(OrgTreeContext);
  const [selectedTeam, setSelectedTeam] = useState<OrgTreeItem>();
  const [teamMembers, setTeamMembers] = useState<UserData[]>();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const DialogWindow = useContext(DialogWindowContext);

  //fetch needed users
  useEffect(() => {
    if (selectedTeam && orgTree) {
      const teamIds = getAllUserIdsFromOrgTreeNode(selectedTeam, [orgTree]);
      if (teamIds) {
        userDatabase?.setUsersNeeded((prevState) => {
          if (!prevState) {
            return [...teamIds];
          } else {
            const filteredTeamIds = teamIds.filter(
              (teamId) => !prevState.includes(teamId)
            );
            return [...prevState, ...filteredTeamIds];
          }
        });
      }
    }
  }, [selectedTeam, orgTree]);

  useEffect(() => {
    if (selectedTeam && users) {
      //filter all teanMembers that are part of the selected team
      const teamMembers = users.filter((user) => {
        return (
          user.PCRoles.filter((role) => role.teamId === selectedTeam.teamId)
            .length > 0
        );
      });
      
      //filter all teanMembers by fristName
      teamMembers.sort((a, b) => {
        if (a.firstName < b.firstName) {
          return -1;
        } else {
          return 1;
        }
      });
      setTeamMembers(teamMembers);
    }
  }, [selectedTeam, userDatabase]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const leaderRoleString = (selectedTeam: OrgTreeItem) => {
    {
      switch (selectedTeam.teamType) {
        case "campus":
          return "Campus Oversight";
        case "country":
          return "National Oversight";
        case "global":
          return "Global Admin";
        case "group":
          return "Group Oversight";
        default:
          return "";
      }
    }
  };

  function addTeamMember() {
    if (selectedTeam) {
      DialogWindow.setDialogProps((prevProps) => ({
        ...prevProps,
        showEditWindow: true,
        header: `New Team Member`,
        children: <AddTeamMemeber selectedTeam={selectedTeam} />,
      }));
    }
  }

  return (
    <ManageTeamsWrapper>
      <OrgTreeSelect teamSelect={setSelectedTeam} />
      {selectedTeam && (
        <>
          <TeamSettings selectedTeam={selectedTeam} />
          <TeamMembersList>
            <Header>Team Members</Header>
            <ColumnHeaders>
              <p>First Name</p>
              <p>Last Name</p>
              {windowWidth > 550 && (
                <p className="tooltip-team-leader">
                  {leaderRoleString(selectedTeam)}
                </p>
              )}
              <Tooltip target=".tooltip-team-leader" autoHide={true}>
                <div className="flex align-items-center">
                  <p>
                    Team Oversights can access the Team Dashboard, have admin
                    access to the team, and can report numbers of weddings.
                  </p>
                </div>
              </Tooltip>
              {windowWidth > 550 && <p>Remove</p>}
              <p>Edit</p>
            </ColumnHeaders>

            <TeamMembersTable>
              {teamMembers &&
                teamMembers.map((member) => {
                  return (
                    <TeamMemberRow
                      key={member.userId}
                      teamMember={member}
                      selectedTeam={selectedTeam}
                    />
                  );
                })}
              <AddMemberRow onClick={addTeamMember}>
                Add team member
              </AddMemberRow>
            </TeamMembersTable>
          </TeamMembersList>
        </>
      )}
    </ManageTeamsWrapper>
  );
}
